import { useState, useRef, useEffect, useCallback } from "react";

/**
 * A textarea that detects @ and shows a mention dropdown.
 * Props:
 *   value, onChange(newText), onMentionsChange(mentionedEmails[])
 *   members — [{ id, name, email, initials }]
 *   placeholder, rows
 */
export default function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  onSubmit,
  members = [],
  placeholder,
  rows = 3,
}) {
  const [query, setQuery] = useState("");          // text after @
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentions, setMentions] = useState([]);    // confirmed mentions
  const textareaRef = useRef();
  const menuRef = useRef();

  const filtered = members.filter((m) =>
    m.name.toLowerCase().includes(query.toLowerCase())
  );

  // Notify parent whenever mentions list changes
  useEffect(() => {
    onMentionsChange && onMentionsChange(mentions.map((m) => m.email));
  }, [mentions, onMentionsChange]);

  // Clear mentions when parent clears the text (e.g. after posting)
  useEffect(() => {
    if (value === "") setMentions([]);
  }, [value]);

  // Position the dropdown near the caret
  const updateMenuPos = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    // Approximate caret position using a mirror div technique
    const style = window.getComputedStyle(ta);
    const mirror = document.createElement("div");
    mirror.style.cssText = `
      position:absolute;visibility:hidden;white-space:pre-wrap;word-wrap:break-word;
      font:${style.font};padding:${style.padding};border:${style.border};
      width:${ta.offsetWidth}px;box-sizing:border-box;
    `;
    const textUpToCaret = ta.value.slice(0, ta.selectionStart);
    mirror.textContent = textUpToCaret;
    const span = document.createElement("span");
    span.textContent = "|";
    mirror.appendChild(span);
    document.body.appendChild(mirror);
    const rect = ta.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    document.body.removeChild(mirror);
    setMenuPos({
      top: spanRect.top - rect.top + ta.scrollTop + 20,
      left: Math.min(spanRect.left - rect.left, ta.offsetWidth - 200),
    });
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);

    // Detect @ trigger
    const cursor = e.target.selectionStart;
    const textBefore = text.slice(0, cursor);
    const atMatch = textBefore.match(/@([\w\s.]*)$/);
    if (atMatch) {
      setQuery(atMatch[1]);
      setActiveIdx(0);
      setMenuOpen(true);
      updateMenuPos();
    } else {
      setMenuOpen(false);
      setQuery("");
    }
  };

  const insertMention = (member) => {
    const ta = textareaRef.current;
    const cursor = ta.selectionStart;
    const textBefore = value.slice(0, cursor);
    const atIdx = textBefore.lastIndexOf("@");
    const before = value.slice(0, atIdx);
    const after = value.slice(cursor);
    const newText = `${before}@${member.name} ${after}`;
    onChange(newText);
    setMentions((prev) => {
      if (prev.find((m) => m.id === member.id)) return prev;
      return [...prev, member];
    });
    setMenuOpen(false);
    setQuery("");
    // Restore focus and move caret to after the inserted mention
    setTimeout(() => {
      ta.focus();
      const pos = atIdx + member.name.length + 2;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    // When the mention menu is closed, let Enter insert a newline normally.
    // Posting is only done via the Post button.
    if (!menuOpen) {
      return;
    }
    // Mention menu navigation
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setMenuOpen(false);
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          e.target !== textareaRef.current) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <textarea
        ref={textareaRef}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {menuOpen && filtered.length > 0 && (
        <div
          ref={menuRef}
          className="mention-menu"
          style={{ bottom: "100%", left: 0, marginBottom: 4 }}
        >
          <div className="mention-menu-header">Team members</div>
          {filtered.map((m, i) => (
            <div
              key={m.id}
              className={`mention-option${i === activeIdx ? " active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <div className="m-avatar">{m.initials}</div>
              <div>
                <div className="m-name">{m.name}</div>
                <div className="m-email">{m.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Render confirmed mentions as chips below the textarea */}
      {mentions.length > 0 && (
        <div className="mention-chips">
          {mentions.map((m) => (
            <span key={m.id} className="mention-chip">
              @{m.name}
              <button
                className="chip-remove"
                onClick={() => setMentions((prev) => prev.filter((x) => x.id !== m.id))}
                aria-label={`Remove mention of ${m.name}`}
              >×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
